import React, { useEffect, useState, useCallback } from 'react';
import {
  LogOut, RefreshCw, Search, Phone, Mail, MapPin, Calendar, Clock,
  CheckCircle2, XCircle, FileDown, Trash2, User, MessageSquare, Tag, ListTodo,
  Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useRequireAdmin } from '../lib/auth';
import { adminApi, authApi } from '../lib/api';
import { companyInfo } from '../data/mock';
import { useCatalogLabels } from '../lib/useCatalog';
import { useNavigate } from 'react-router-dom';
import BlogAdmin from '../components/BlogAdmin';
import ServicesAdmin from '../components/ServicesAdmin';
import ClientsAdmin from '../components/ClientsAdmin';
import AvailabilityAdmin from '../components/AvailabilityAdmin';
import PlanningAdmin from '../components/PlanningAdmin';
import { GoogleCalendarCard } from '../components/GoogleCalendarCard';
import { ReservationTools } from '../components/ReservationTools';
import { ClientDetailDialog } from '../components/ClientDetailDialog';
import { QuoteHistory } from '../components/QuoteHistory';
import { clientsApi } from '../lib/api';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const STATUS_META = {
  pending:   { label: 'En attente', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  confirmed: { label: 'Confirmé',   cls: 'bg-blue-50 text-blue-700 border-blue-200',     dot: 'bg-blue-500' },
  completed: { label: 'Terminé',    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  cancelled: { label: 'Annulé',     cls: 'bg-rose-50 text-rose-700 border-rose-200',     dot: 'bg-rose-500' },
};


const FRENCH_MONTHS = ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];
const formatDate = (s) => {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  return `${parseInt(d)} ${FRENCH_MONTHS[parseInt(m) - 1]} ${y}`;
};
const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 30) return `il y a ${days} j`;
  return d.toLocaleDateString('fr-FR');
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${meta.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const StatCard = ({ label, value, accent = 'blue' }) => {
  const palette = {
    blue:    'from-blue-50 to-blue-100 text-blue-700',
    amber:   'from-amber-50 to-amber-100 text-amber-700',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700',
    rose:    'from-rose-50 to-rose-100 text-rose-700',
    orange:  'from-orange-50 to-orange-100 text-orange-700',
  }[accent];
  return (
    <div className={`bg-gradient-to-br ${palette} rounded-2xl p-5 border border-white/50`} data-testid={`stat-${label.replace(/\s+/g,'-').toLowerCase()}`}>
      <p className="text-xs font-bold uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-3xl font-bold mt-2">{value}</p>
    </div>
  );
};

const AdminDashboard = () => {
  const labels = useCatalogLabels();
  const { user, loading } = useRequireAdmin();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('quotes'); // 'quotes' | 'blog'
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [clientsRefresh, setClientsRefresh] = useState(0);
  const closeClient = useCallback(() => setClientId(null), []);
  const openClientOfQuote = async (quoteId) => {
    try {
      const res = await clientsApi.byQuote(quoteId);
      setSelected(null);
      setClientId(res.data.client_id);
    } catch { toast.error('Aucune fiche client pour ce devis'); }
  };
  const [confirmDelete, setConfirmDelete] = useState(null);

  useDocumentMeta({
    title: 'Tableau de bord — SMT Services',
    description: 'Espace administrateur SMT Services.',
    path: '/admin',
    noindex: true,
  });

  const fetchAll = useCallback(async (status = 'all') => {
    try {
      setRefreshing(true);
      const [qRes, sRes] = await Promise.all([
        adminApi.listQuotes(status),
        adminApi.stats(),
      ]);
      setQuotes(qRes.data);
      setStats(sRes.data);
    } catch (err) {
      toast.error('Erreur de chargement');
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchAll(filter);
  }, [user, filter, fetchAll]);

  const handleStatusChange = async (id, status) => {
    try {
      const res = await adminApi.updateStatus(id, status);
      toast.success('Statut mis à jour');
      if (status === 'completed' && res.data?.review_email_sent_at) {
        toast.success("📧 Email d'avis Google envoyé au client", { duration: 4500 });
      }
      fetchAll(filter);
      setSelected((prev) => prev && prev.id === id ? { ...prev, ...res.data } : prev);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec de la mise à jour');
    }
  };

  const handleResendReviewEmail = async (id) => {
    try {
      await adminApi.sendReviewEmail(id);
      toast.success("📧 Email d'avis Google envoyé");
      const fresh = await adminApi.getQuote(id);
      setSelected(fresh.data);
      fetchAll(filter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteQuote(id);
      toast.success('Demande supprimée');
      setSelected(null);
      setConfirmDelete(null);
      fetchAll(filter);
    } catch {
      toast.error('Échec de la suppression');
    }
  };

  const handleLogout = async () => {
    try { await authApi.logout(); } catch (_) { /* no-op */ }
    navigate('/admin/login', { replace: true });
  };

  const handleExport = () => {
    window.open(adminApi.exportUrl(), '_blank');
  };

  const filteredQuotes = quotes.filter((q) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      `${q.first_name} ${q.last_name}`.toLowerCase().includes(s) ||
      q.email?.toLowerCase().includes(s) ||
      q.phone?.includes(s) ||
      q.city?.toLowerCase().includes(s)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[hsl(207,90%,54%)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return null;

  const filterTabs = [
    { id: 'all',       label: 'Tous',      count: stats?.total ?? 0 },
    { id: 'pending',   label: 'En attente',count: stats?.by_status.pending ?? 0 },
    { id: 'confirmed', label: 'Confirmés', count: stats?.by_status.confirmed ?? 0 },
    { id: 'completed', label: 'Terminés',  count: stats?.by_status.completed ?? 0 },
    { id: 'cancelled', label: 'Annulés',   count: stats?.by_status.cancelled ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={companyInfo.logo} alt="logo" className="h-10 w-auto" />
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Admin</p>
              <p className="font-bold text-slate-900 leading-none">{companyInfo.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-slate-700">{user.name}</span>
              <span className="text-xs text-slate-400">{user.email}</span>
            </div>
            {user.picture && (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full ring-2 ring-blue-100" />
            )}
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 overflow-x-auto whitespace-nowrap">
          <button
            onClick={() => setActiveTab('quotes')}
            data-testid="tab-quotes"
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'quotes'
                ? 'border-[hsl(207,90%,54%)] text-[hsl(207,90%,54%)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Demandes de devis
          </button>
          <button
            onClick={() => setActiveTab('blog')}
            data-testid="tab-blog"
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'blog'
                ? 'border-[hsl(207,90%,54%)] text-[hsl(207,90%,54%)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Articles de blog
          </button>
          <button
            onClick={() => setActiveTab('services')}
            data-testid="tab-services"
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'services'
                ? 'border-[hsl(207,90%,54%)] text-[hsl(207,90%,54%)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Prestations
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            data-testid="tab-clients"
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'clients'
                ? 'border-[hsl(207,90%,54%)] text-[hsl(207,90%,54%)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Clients
          </button>
          <button
            onClick={() => setActiveTab('availability')}
            data-testid="tab-availability"
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'availability'
                ? 'border-[hsl(207,90%,54%)] text-[hsl(207,90%,54%)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Disponibilités
          </button>
          <button
            onClick={() => setActiveTab('planning')}
            data-testid="tab-planning"
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === 'planning'
                ? 'border-[hsl(207,90%,54%)] text-[hsl(207,90%,54%)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Planning
          </button>
        </div>

        {activeTab === 'planning' ? (
          <div className="space-y-4">
            <GoogleCalendarCard />
            <PlanningAdmin labels={labels.services} onOpenQuote={setSelected} refreshKey={`${selected?.id || ''}-${selected?.status || ''}-${selected?.updated_at || ''}`} />
          </div>
        ) : activeTab === 'availability' ? (
          <AvailabilityAdmin />
        ) : activeTab === 'clients' ? (
          <ClientsAdmin onOpenClient={setClientId} refreshKey={clientsRefresh} />
        ) : activeTab === 'services' ? (
          <ServicesAdmin />
        ) : activeTab === 'blog' ? (
          <BlogAdmin />
        ) : (
          <>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Total reçus" value={stats.total} accent="blue" />
            <StatCard label="Ce mois" value={stats.this_month} accent="orange" />
            <StatCard label="En attente" value={stats.by_status.pending} accent="amber" />
            <StatCard label="Confirmés" value={stats.by_status.confirmed} accent="blue" />
            <StatCard label="Conversion" value={`${stats.conversion_rate}%`} accent="emerald" />
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex flex-wrap gap-2">
            {filterTabs.map((t) => (
              <button
                key={t.id}
                data-testid={`filter-${t.id}`}
                onClick={() => setFilter(t.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  filter === t.id
                    ? 'bg-[hsl(207,90%,54%)] text-white shadow'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {t.label} <span className="opacity-70">· {t.count}</span>
              </button>
            ))}
          </div>
          <div className="flex-1 flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder="Rechercher (nom, email, téléphone, ville)…"
                className="pl-9 bg-slate-50 border-slate-200"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchAll(filter)}
              disabled={refreshing}
              data-testid="refresh-button"
              title="Rafraîchir"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={handleExport}
              data-testid="export-button"
              variant="outline"
              className="gap-1.5"
            >
              <FileDown className="w-4 h-4" />
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
          </div>
        </div>

        {/* Quotes table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {filteredQuotes.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <ListTodo className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">Aucune demande {search ? 'ne correspond' : 'pour ce filtre'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="quotes-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-slate-500 uppercase text-[11px] tracking-wider">
                    <th className="px-4 py-3 font-semibold">Client</th>
                    <th className="px-4 py-3 font-semibold hidden md:table-cell">Contact</th>
                    <th className="px-4 py-3 font-semibold">Créneau</th>
                    <th className="px-4 py-3 font-semibold hidden lg:table-cell">Reçu</th>
                    <th className="px-4 py-3 font-semibold">Statut</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredQuotes.map((q) => (
                    <tr
                      key={q.id}
                      className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                      onClick={() => setSelected(q)}
                      data-testid={`quote-row-${q.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{q.first_name} {q.last_name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" /> {q.city}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="text-slate-700 text-xs">{q.email}</div>
                        <div className="text-slate-500 text-xs mt-0.5">{q.phone}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{formatDate(q.date)}</div>
                        <div className="text-xs text-slate-500">{q.time}</div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">
                        {formatRelative(q.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={q.status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <a
                            href={`tel:${(q.phone || '').replace(/\s/g, '')}`}
                            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-blue-100 hover:text-blue-700 flex items-center justify-center transition-colors"
                            title="Appeler"
                            data-testid={`call-${q.id}`}
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                          <a
                            href={`mailto:${q.email}`}
                            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-blue-100 hover:text-blue-700 flex items-center justify-center transition-colors"
                            title="Envoyer email"
                            data-testid={`email-${q.id}`}
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </span>
                  <span>{selected.first_name} {selected.last_name}</span>
                  <StatusBadge status={selected.status} />
                </DialogTitle>
                <DialogDescription>
                  Demande reçue {formatRelative(selected.created_at)} · ID {selected.id.slice(0, 8)}
                  <button type="button" onClick={() => openClientOfQuote(selected.id)} data-testid="open-client-card-button"
                    className="ml-2 font-semibold text-[hsl(207,90%,44%)] hover:underline">Voir la fiche client</button>
                </DialogDescription>
              </DialogHeader>

              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <a href={`tel:${(selected.phone || '').replace(/\s/g, '')}`}
                   className="flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors">
                  <Phone className="w-5 h-5 text-blue-700" />
                  <div>
                    <div className="text-xs text-blue-600 uppercase font-bold">Téléphone</div>
                    <div className="font-semibold text-slate-900">{selected.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${selected.email}`}
                   className="flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors">
                  <Mail className="w-5 h-5 text-blue-700" />
                  <div>
                    <div className="text-xs text-blue-600 uppercase font-bold">Email</div>
                    <div className="font-semibold text-slate-900 truncate">{selected.email}</div>
                  </div>
                </a>
              </div>

              <div className="mt-3 p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-xs uppercase font-bold text-slate-500 mb-1">
                  <MapPin className="w-3.5 h-3.5" /> Adresse
                </div>
                <div className="text-slate-900">{selected.address}, {selected.city}</div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div className="p-3 bg-amber-50 rounded-xl">
                  <div className="flex items-center gap-2 text-xs uppercase font-bold text-amber-700 mb-1">
                    <Calendar className="w-3.5 h-3.5" /> Date souhaitée
                  </div>
                  <div className="font-semibold text-slate-900">{formatDate(selected.date)}</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl">
                  <div className="flex items-center gap-2 text-xs uppercase font-bold text-amber-700 mb-1">
                    <Clock className="w-3.5 h-3.5" /> Créneau
                  </div>
                  <div className="font-semibold text-slate-900">{selected.time}</div>
                </div>
              </div>

              <div className="mt-3">
                <div className="flex items-center gap-2 text-xs uppercase font-bold text-slate-500 mb-2">
                  <Tag className="w-3.5 h-3.5" /> Services demandés
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selected.services.map((s) => (
                    <span key={s} className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                      {labels.services[s] || s}
                    </span>
                  ))}
                </div>
              </div>

              {selected.options?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase font-bold text-slate-500 mb-2">Options</div>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.options.map((o) => (
                      <span key={o} className="px-2.5 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">
                        {labels.options[o] || o}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <ReservationTools q={selected} onUpdated={(d) => { setSelected((p) => ({ ...p, ...d })); fetchAll(filter); }} />

              <QuoteHistory quoteId={selected.id} refreshKey={`${selected.status}-${selected.review_email_sent_at || ''}-${selected.updated_at || ''}-${selected.gcal?.state || ''}-${selected.gcal?.attempts || 0}`} />

              {selected.message && (
                <div className="mt-3 p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-xs uppercase font-bold text-slate-500 mb-1">
                    <MessageSquare className="w-3.5 h-3.5" /> Message du client
                  </div>
                  <p className="text-slate-700 italic whitespace-pre-wrap">{selected.message}</p>
                </div>
              )}

              {/* Action bar */}
              <div className="mt-5 pt-4 border-t border-slate-200">
                <p className="text-xs uppercase font-bold text-slate-500 mb-2">Mettre à jour le statut</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(STATUS_META).map(([s, meta]) => (
                    <Button
                      key={s}
                      data-testid={`set-status-${s}`}
                      onClick={() => handleStatusChange(selected.id, s)}
                      disabled={selected.status === s}
                      variant={selected.status === s ? 'default' : 'outline'}
                      size="sm"
                      className={`gap-1.5 ${selected.status === s ? 'pointer-events-none opacity-60' : ''}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                      {meta.label}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="send-review-email-button"
                  onClick={() => handleResendReviewEmail(selected.id)}
                  className="mt-3 w-full text-blue-700 border-blue-200 hover:bg-blue-50 gap-1.5"
                >
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-500" />
                  {selected.review_email_sent_at
                    ? `Renvoyer l'email d'avis Google (déjà envoyé ${formatRelative(selected.review_email_sent_at)})`
                    : 'Envoyer l\'email d\'avis Google maintenant'}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  data-testid="delete-quote-button"
                  onClick={() => setConfirmDelete(selected)}
                  className="mt-3 w-full text-rose-700 border-rose-200 hover:bg-rose-50 gap-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer définitivement la demande
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ClientDetailDialog clientId={clientId} labels={labels.services} onClose={closeClient}
        onOpenQuote={(q) => { setClientId(null); setSelected(q); }} onChanged={() => setClientsRefresh((n) => n + 1)} />

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette demande ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le créneau sera également libéré.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={() => handleDelete(confirmDelete.id)}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
